<template>
    <CollapsibleCard
        class="regulation-review-card mb-1"
        body-class="p-1"
        :default-collapsed="false"
    >
        <template #title>
            <div class="regulation-review-card__title">
                <span class="regulation-review-card__number">{{ number }}.</span>
                <div class="min-width-0">
                    <div class="d-flex flex-wrap align-items-center">
                        <strong>{{ regulation.name || translate('Component(ResearchReviewProcess):unnamed-legislation', 'Unnamed legislation') }}</strong>
                        <BBadge
                            v-if="regulation.status"
                            variant="light-secondary"
                            class="ml-50"
                        >
                            {{ statusLabel(regulation.status) }}
                        </BBadge>
                    </div>
                    <small class="text-less-muted">
                        {{ summaryText }}
                    </small>
                </div>
            </div>
        </template>

        <template #actions>
            <ReviewNeedsAttentionBadge :checks="evaluation.checks" />
            <BButton
                type="button"
                variant="flat-danger"
                size="sm"
                class="btn-icon mr-50"
                :class="{ 'ml-50': evaluation.status === ReviewCheckStatus.NEEDS_REVIEW }"
                :title="translate('Component(Button):remove', 'Remove')"
                @click.stop="$emit('remove')"
            >
                <FeatherIcon icon="Trash2Icon" size="16" />
            </BButton>
        </template>

        <BRow>
            <BCol cols="12" md="6">
                <BFormGroup :label="translate('Component(ResearchReviewProcess):legislation-name', 'Legislation name')">
                    <BFormInput
                        :value="regulation.name"
                        @input="updateRegulation('name', $event)"
                    />
                </BFormGroup>
            </BCol>
            <BCol cols="12" md="6">
                <BFormGroup :label="translate('Component(ResearchReviewProcess):normalized-legislation', 'Normalized legislation')">
                    <VSelect
                        :value="selectedOption(legislationKeyOptions, regulation.key)"
                        :options="legislationKeyOptions"
                        label="label"
                        :clearable="false"
                        :append-to-body="true"
                        :selectable="isLegislationOptionSelectable"
                        :placeholder="translate('Component(ResearchReviewProcess):select-legislation', 'Select legislation')"
                        @input="updateRegulation('key', $event ? $event.id : null)"
                    />
                </BFormGroup>
            </BCol>
            <BCol cols="12" md="6">
                <BFormGroup :label="translate('Component(ComplianceOverview):column-status', 'Coverage status')">
                    <VSelect
                        :value="selectedOption(statusOptions, regulation.status)"
                        :options="statusOptions"
                        label="label"
                        :clearable="false"
                        :append-to-body="true"
                        :placeholder="translate('Component(ResearchReviewProcess):select-coverage-status', 'Select coverage status')"
                        @input="updateRegulation('status', $event ? $event.id : null)"
                    />
                </BFormGroup>
            </BCol>
            <BCol cols="12" md="6">
                <BFormGroup :label="translate('Component(ResearchReviewProcess):evidence', 'Evidence')">
                    <BFormTextarea
                        :value="regulation.evidence"
                        rows="2"
                        max-rows="5"
                        :placeholder="translate('Component(ResearchReviewProcess):evidence-placeholder', 'Source text or page reference')"
                        @input="updateRegulation('evidence', $event || null)"
                    />
                </BFormGroup>
            </BCol>
        </BRow>

        <div class="regulation-review-card__subsection">
            <div class="regulation-review-card__subsection-heading">
                <div>
                    <h6 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):claims', 'Claims') }}
                    </h6>
                    <small class="review-section-description text-less-muted">
                        {{ translate('Component(ResearchReviewProcess):claims-description', 'Confirm the statements extracted for this legislation.') }}
                    </small>
                </div>
                <BButton
                    v-if="usesStructuredClaimsEditor"
                    type="button"
                    variant="outline-primary"
                    @click="addClaim"
                >
                    <FeatherIcon icon="PlusIcon" size="14" class="mr-25" />
                    {{ translate('Component(ResearchReviewProcess):add-claim', 'Add claim') }}
                </BButton>
            </div>

            <component
                :is="claimsEditorComponent"
                :claims="claims"
                @input="updateClaims"
            />
        </div>

        <div v-if="relatedSubstances.length" class="regulation-review-card__subsection">
            <RegulationRelatedSubstances :items="relatedSubstances" />
        </div>

        <div class="regulation-review-card__subsection">
            <div class="regulation-review-card__subsection-heading">
                <div>
                    <h6 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):exemptions', 'Exemptions') }}
                    </h6>
                    <small class="review-section-description text-less-muted">
                        {{ translate('Component(ResearchReviewProcess):exemptions-description', 'Review exemptions associated with this legislation.') }}
                    </small>
                </div>
                <BButton type="button" variant="outline-primary" @click="$emit('add-exemption')">
                    <FeatherIcon icon="PlusIcon" size="14" class="mr-25" />
                    {{ translate('Component(ResearchReviewProcess):add-exemption', 'Add exemption') }}
                </BButton>
            </div>

            <BAlert v-if="!exemptions.length" variant="light-secondary" show class="mb-0">
                <div class="alert-body">
                    {{ translate('Component(ResearchReviewProcess):no-exemptions', 'No exemptions were extracted for this legislation.') }}
                </div>
            </BAlert>

            <div v-else class="regulation-review-card__exemptions">
                <RegulationExemptionRow
                    v-for="exemption in exemptions"
                    :key="`exemption-${exemption.sourceIndex}`"
                    :value="exemption.value"
                    @input="$emit('update-exemption', { sourceIndex: exemption.sourceIndex, value: $event })"
                    @remove="$emit('remove-exemption', exemption.sourceIndex)"
                />
            </div>
        </div>
    </CollapsibleCard>
</template>

<script>
import {
    BAlert,
    BBadge,
    BButton,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
} from 'bootstrap-vue'
import VSelect from 'vue-select'
import CollapsibleCard from '@core/components/common/CollapsibleCard.vue'
import ReviewNeedsAttentionBadge from '../components/ReviewNeedsAttentionBadge.vue'
import RegulationExemptionRow from './RegulationExemptionRow.vue'
import RegulationRelatedSubstances from './RegulationRelatedSubstances.vue'
import regulationReviewCardMixin from './regulation-review-card-mixin'

export default {
    name: 'RegulationReviewCard',
    mixins: [regulationReviewCardMixin],
    components: {
        BAlert,
        BBadge,
        BButton,
        BCol,
        BFormGroup,
        BFormInput,
        BFormTextarea,
        BRow,
        CollapsibleCard,
        RegulationExemptionRow,
        RegulationRelatedSubstances,
        ReviewNeedsAttentionBadge,
        VSelect,
    },
}
</script>

<style lang="scss" scoped>
.regulation-review-card__title {
    display: flex;
    align-items: flex-start;
    gap: .75rem;
}

.regulation-review-card__number {
    min-width: 1.25rem;
    font-weight: 600;
}

.regulation-review-card__subsection {
    padding-top: 1rem;
    margin-top: .25rem;
    border-top: 1px solid #ebe9f1;

    & + & {
        margin-top: 1rem;
    }
}

.regulation-review-card__subsection-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: .75rem;
}

.regulation-review-card__exemptions {
    display: grid;
    gap: .75rem;
}

.min-width-0 {
    min-width: 0;
}

@media (max-width: 767.98px) {
    .regulation-review-card__subsection-heading {
        align-items: stretch;
        flex-direction: column;
    }
}
</style>
